import { Injectable } from '@angular/core';

import {
  Person,
  PersonEditorCompanyConfig,
  PersonEditorEnums,
  PersonEditorForms
} from '../models/person';
import { EmployeeTaxes } from '../models/employee-tax';
import { ErrorDetail, CTAErrorDetail, WarningLevel } from '../models/error-detail';
import { EmployeeStatus } from '../models/person-status';
import { ModalService } from './modal.service';
import { FlattenResponseMessages, GetResponseModalStyle } from '../utils/api-response-helper';
import { Client } from '../models/client';
import { Observable, of } from 'rxjs';
import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { payrollGridRouteRegex } from '../utils/regex-patterns';
import { SharedService } from './shared.service';
import { PayrollService } from './payroll.service';
import { PayrollStatuses } from '../models/payroll';
import { PersonInitService } from './person-init.service';
import { PersonSaveService } from './person-save.service';
import { PeopleService } from './people.service';
import { CompanyTaxCodeConfig } from '../models/tax-config';
import { Store } from '@ngxs/store';
import {
  IPersonEditorState,
  PersonEditorState
} from '../state/PersonEditorState/PersonEditor.state';
import { WorkersCompCode } from '../workers-comp';
import { ClientSelectionState } from '../state/ClientSelectionState/ClientSelection.state';

@Injectable({
  providedIn: 'root'
})
// responsible for all of the people editor validation logic
export class PersonValidationService {
  constructor(
    private modalService: ModalService,
    private payrollService: PayrollService,
    private peopleService: PeopleService,
    private saveService: PersonSaveService,
    private sharedService: SharedService,
    private store: Store
  ) {}
  // fires on page load and status change
  public checkPayrollValidity(state: IPersonEditorState) {
    this.store.selectOnce(ClientSelectionState.client).subscribe(client => {
      const taxErrors = this.validateTaxElections(state);
      const wcErrors = this.validateWorkersComp(state, client);

      if (taxErrors || wcErrors) {
        this.modalService.showAlertModal(
          FlattenResponseMessages([taxErrors, wcErrors]),
          GetResponseModalStyle([taxErrors, wcErrors])
        );
      }
    });
  }

  public isTabDisabledAtIndex(index: number, tabElemIds): boolean {
    const query = `[id="${tabElemIds[index]}"] a.nav-link`;
    const elem = document.querySelectorAll(query);
    if (elem.length) {
      return elem[0].getAttribute('class').indexOf('disabled') > -1;
    } else {
      // tab link could not be found. possibly hidden by ngIf condition
      // return true so this tab is skipped
      return true;
    }
  }

  public onEditorLeave(
    addToPayrollRoute: string,
    nextStateUrl: string,
    ddFormDirty: boolean = false
  ): Observable<boolean> {
    return this.store.selectOnce(PersonEditorState.getState).pipe(
      mergeMap(state => {
        return this.store.selectOnce(ClientSelectionState.client).pipe(
          map(client => {
            return { state, client };
          })
        );
      }),
      mergeMap(vals => {
        const taxError = this.validateTaxElections(vals.state);

        const wcError = this.validateWorkersComp(vals.state, vals.client);

        if (!PersonEditorState.areFormsPristine(vals.state).All || ddFormDirty) {
          const msg =
            'You have unsaved changes. Are you sure you want to leave this page? Unsaved changes will be lost.';
          this.modalService.showConfirmModal(
            msg,
            'Stay On Page',
            'Leave Page',
            'Are you sure you want to leave this page?'
          );
          return this.modalService.confirmModalCallback.pipe(
            switchMap((confirm): Observable<boolean> => {
              if (confirm) {
                return this._checkAndSetPayrollStatus(addToPayrollRoute, nextStateUrl);
              } else {
                return of(false);
              }
            })
          );
        } else if (taxError || wcError) {
          // US 5092
          // when tax elections no longer match, pop nag modal when user leaves person record
          // only diplay for people in active status
          // ----
          // task 10159 - show warning if workers comp is invalid.
          // if person is invalid for workers comp and tax elections,
          // show both warnings in 1 modal
          const msg = FlattenResponseMessages([taxError, wcError], true);
          this.modalService.showConfirmModal(msg, 'Stay On Page', 'Leave Page');
          return this.modalService.confirmModalCallback.pipe(
            switchMap((confirm): Observable<boolean> => {
              if (confirm) {
                return this._checkAndSetPayrollStatus(addToPayrollRoute, nextStateUrl);
              } else {
                return of(false);
              }
            })
          );
        } else {
          // there are no unsaved changes.
          // check if the user came from the payroll grid, then
          // let user leave page without harassment
          return this._checkAndSetPayrollStatus(addToPayrollRoute, nextStateUrl);
        }
      })
    );
  }

  // check if the selected state on that taxes tab
  // matches both the resident and work state
  public validateTaxElections(state: IPersonEditorState): ErrorDetail {
    // if this person is not invalid to pay
    // because of tax elections, stop here
    if (!state.Person.InvalidToPayDetails?.find(x => x.FieldName == 'Taxes')) {
      return null;
    }

    let stateMsg = '';
    const whState = state.Forms.TaxesForm.model.WithHoldingState;
    const fname = state.Forms.PersonalDetailsForm.model.FirstName;
    const lname = state.Forms.PersonalDetailsForm.model.LastName;

    const elections = state.PersonLists.Taxes.FedAndState.filter(x => {
      const config = state.CompanyConfigs.TaxConfigs.find(c => c.TaxCode === x.TaxCode);
      return x.IsActive && config && config.EntityType === 'S';
    });

    if (elections.length > 1) {
      // if selection is Both, we should have two active state tax elections in the model
      stateMsg = `${elections[0].StateAbbreviation} and ${elections[1].StateAbbreviation}`;
    } else if (whState && whState.indexOf('Both') === -1) {
      stateMsg = whState;
    }

    if (
      !stateMsg ||
      !state.PersonLists.Statuses[0] ||
      state.PersonLists.Statuses[0].StatusId != 0
    ) {
      // person is not active or there was a problem finding the tax withholding state(s)
      return null;
    }

    const name = `${fname} ${lname}`.trim();

    if (
      elections.length == 1 &&
      [state.Person.WorksIn, state.Person.State].includes(elections[0].StateAbbreviation)
    ) {
      // withholding state matches either resident or work state
      // show message that user 'may' need to update taxes
      const msg = `${name} is currently withholding income tax in ${stateMsg}. Withholding taxes may need to be updated on the Taxes screen before paying this person in payroll.`;
      return new ErrorDetail('', msg, WarningLevel.Warning);
    } else {
      // withholding state does not match resident or work state
      // alert user that they must update the person's taxes before including in payroll
      const msg = `${name} is currently withholding income tax in ${stateMsg}. You must visit the Taxes screen and update the state withholding tax elections before this person can be paid in payroll.`;
      return new CTAErrorDetail('', msg, WarningLevel.Warning, 'Action Required');
    }
  }

  // task 10159
  // invalid under the follow scenario:
  // the client is setup for workers compensation,
  // but the (active) employee's work state has no
  // workers comp codes setup on that client
  public validateWorkersComp(state: IPersonEditorState, client: Client): ErrorDetail {
    if (!state.Person.InvalidToPayDetails?.find(x => x.FieldName == 'WorkersComp')) {
      return null;
    }

    if (
      client.HasWorkersCompensation &&
      client.RequiresWorkersCompCodes &&
      state.PersonLists.Statuses[0] &&
      state.PersonLists.Statuses[0].StatusId == 0 /*active status */
    ) {
      const w = state.Forms.JobDetailsForm.model.WorksIn;

      if (!w) {
        return null;
      }

      // bug 10483
      // check if there are any available comp codes for the selected
      // works in state in order to construct consistent messaging
      // with the workers comp field level error
      const availableCompCodes = state.CompanyConfigs.WorkersCompCodes.filter(
        x => x.StateCode === w
      );

      if (!availableCompCodes.length) {
        return new ErrorDetail(
          '',
          `Workers Compensation codes for ${w} must be added to the Company Workers Compensation codes list`,
          WarningLevel.Warning
        );
      } else if (!state.Forms.JobDetailsForm.model.WorkersCompCode) {
        return new ErrorDetail(
          '',
          `${state.Person.FirstName} ${state.Person.LastName} is missing a Workers Compensation code which is required for this company. You must visit the Job Details screen and update the Workers Compensation rules before this person can be paid in payroll.`,
          WarningLevel.Warning
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  // task 9739
  // from payroll (indicated by the addToPayroll query param)
  // if the user navigated to the create person screen
  // AND they are navigating away from this screen and going
  // anywhere that is not the payroll grid -
  // set the status of the payroll to 'On Hold'
  private _checkAndSetPayrollStatus(addToPayrollRoute: string, nextStateUrl: string) {
    // if this query param isn't present, the user did not come from the payroll grid.
    // do nothing.
    if (!addToPayrollRoute) {
      return of(true);
    }

    // if nextStateUrl is null, the person editor is being operated inside a modal
    // such as on the payroll grid
    if (!nextStateUrl) {
      return of(true);
    }

    const payrollInfo = this.sharedService.getPayrollStatusInfo();

    // if no payroll info was found in the shared service
    // or if the user is navigating back to the payroll grid
    // do nothing.
    if (!payrollInfo || payrollGridRouteRegex.test(nextStateUrl)) {
      return of(true);
    }

    // user is going somewhere that is not the payroll grid
    // set the payroll status to on hold
    return this.payrollService
      .updatePayrollStatus(
        payrollInfo.ClientCode,
        payrollInfo.Year,
        payrollInfo.SequenceNumber,
        PayrollStatuses.Hold
      )
      .pipe(
        concatMap(resp => {
          this.sharedService.setPayrollStatusInfo(null);
          return of(true);
        })
      );
  }
}
